import React, { useState, useEffect } from 'react';
import { adminApi } from '../../services/api';
import PaginationControls from '../../components/PaginationControls';
import AgGridTable from '../../components/AgGridTable';

const ApiTokenManager = ({ user, onClose }) => {
  const [tokens, setTokens] = useState([]);
  const [accessLogs, setAccessLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newToken, setNewToken] = useState(null);
  const [copySuccess, setCopySuccess] = useState('');
  const [copiedTokenId, setCopiedTokenId] = useState(null);
  const [accessLogsPagination, setAccessLogsPagination] = useState({});
  const [accessLogsLoading, setAccessLogsLoading] = useState(false);

  const fetchTokens = async () => {
    try {
      setLoading(true);
      const response = await adminApi.get(`/admin/mfa/users/${user.id}/tokens/`);
      if (response.data && response.data.success) {
        setTokens(response.data.api_tokens);
      } else {
        setError(response.data?.message || 'Failed to fetch API tokens');
      }
    } catch (err) {
      console.error('Error fetching API tokens:', err);
      setError(err.message || 'An error occurred while fetching API tokens');
    } finally {
      setLoading(false);
    }
  };

  const fetchAccessLogs = async (page = 1) => {
    setAccessLogsLoading(true);
    try {
      const response = await adminApi.get(`/admin/mfa/users/${user.id}/logs/`, {
        params: { filter: 'api_token', page: page }
      });
      if (response.data && response.data.success) {
        // Handle both possible response formats
        const logs = response.data.data || response.data.access_logs || [];
        setAccessLogs(logs);
        if (response.data.pagination) {
          setAccessLogsPagination(response.data.pagination);
        }
      } else {
        setError(response.data?.message || 'Failed to fetch access logs');
      }
    } catch (err) {
      console.error('Error fetching access logs:', err);
      setError(err.message || 'An error occurred while fetching access logs');
    } finally {
      setAccessLogsLoading(false);
    }
  };

  const handleAccessLogsPageChange = (page) => { fetchAccessLogs(page); };
 
   const handleCopy = (textToCopy, isNewToken = false, tokenId = null) => {
     navigator.clipboard.writeText(textToCopy).then(() => {
       if (isNewToken) {
         setCopySuccess('Copied!');
         setTimeout(() => setCopySuccess(''), 2000);
       } else {
         setCopiedTokenId(tokenId);
         setTimeout(() => setCopiedTokenId(null), 2000);
       }
     }).catch(err => {
       console.error('Failed to copy: ', err);
       if (isNewToken) {
         setCopySuccess('Failed to copy!');
         setTimeout(() => setCopySuccess(''), 2000);
       } else {
         setCopiedTokenId(null); // Clear on failure
       }
     });
   };

   const handleCopyToken = () => {
     handleCopy(newToken, true); // Pass true for isNewToken
   };
 
   useEffect(() => {
     if (user) {
       fetchTokens();
       fetchAccessLogs(1); // Fetch access logs when user changes
     }
   }, [user]);

  const handleGenerateToken = async () => {
    try {
      const response = await adminApi.post('/admin/token/mfa', {
        api_token_type: 'external_2fa_client',
        description: `API token for ${user.email}`,
        mfa_user_id: user.id,
        created_by: user.email,
      });

      if (response.data && response.data.api_token) {
        setNewToken(response.data.api_token);
        fetchTokens();
        fetchAccessLogs(1);
      } else {
        setError(response.data?.message || 'Failed to generate token');
      }
    } catch (err) {
      if (err.response && err.response.data && err.response.data.error) {
        setError(err.response.data.error);
      } else if (err.response && err.response.status === 403) {
        setError('You do not have permission to perform this action.'); // Generic 403 if no specific error message from API
      } else {
        console.error('Error generating API token:', err);
        setError(err.message || 'An unexpected error occurred.');
      }
    }
  };

  const handleDeactivateToken = async (tokenId) => {
    try {
      await adminApi.patch(`/admin/token/${tokenId}/deactivate/`);
      fetchTokens();
      fetchAccessLogs(1);
    } catch (err) {
      console.error('Error deactivating token:', err);
      setError(err.response?.data?.error || 'Failed to deactivate token.');
    }
  };

  const handleDeleteToken = async (tokenId) => {
    try {
      await adminApi.delete(`/admin/token/${tokenId}/delete/`);
      fetchTokens();
      fetchAccessLogs(1);
    } catch (err) {
      console.error('Error deleting token:', err);
      setError(err.response?.data?.error || 'Failed to delete token.');
    }
  };

  const handleReactivateToken = async (tokenId) => {
    try {
      await adminApi.patch(`/admin/token/${tokenId}/activate/`);
      fetchTokens();
      fetchAccessLogs(1);
    } catch (err) {
      console.error('Error reactivating token:', err);
      setError(err.response?.data?.error || 'Failed to reactivate token.');
    }
  };

  if (!user) {
    return null;
  }

  if (loading) {
    return <div>Loading tokens...</div>;
  }

  return (
    <div className="card">
      <div className="card-body">
      <div className="card-title text-primary mb-3">API Tokens for {user.email}</div>

      {error && <div className="alert alert-danger">{error}</div>}

      {newToken && (
        <div className="alert alert-info mt-3">
          <span className="card-title fw-bold mb-3">New API Token</span> 
                      <h3 className="mt-3">{newToken}
                      <button className="btn" onClick={handleCopyToken} title="Copy OTP">
                        <i className="fas fa-copy"></i>
                      </button></h3>
                      {copySuccess && <small className="text-success">{copySuccess}</small>}
                      <p>This token will not be shown again. Please copy and store it securely.</p>
                    </div>
      )}

      {!newToken && (
        user.is_enabled ? (
          <button
            className="btn btn-primary mb-3"
            onClick={handleGenerateToken}
            title="Generate a new API token for this MFA user."
          >
            Generate New Token
          </button>
        ) : (
          <p className="text-center text-secondary">Can&apos;t generate API token for this disabled MFA user</p>
        )
      )}

      </div>
        <div className="card-body">
        <span className="card-title text-primary mb-3">Existing Tokens</span>
        <div>
        {tokens.length === 0 ? (
            <p>No API tokens found for this user.</p>
        ) : (
            <AgGridTable
              rowData={tokens}
              height={400}
              columnDefs={[
                {
                  headerName: 'ID',
                  field: 'api_token_id',
                  width: 180,
                  cellRenderer: (params) => {
                    if (!params.value) return null;
                    return (
                      <div>
                        <button 
                          className="btn btn-link p-0 me-2" 
                          onClick={() => handleCopy(params.value, false, params.value)} 
                          title="Copy Token ID"
                        >
                          <i className="fas fa-copy"></i>
                        </button>
                        {copiedTokenId === params.value && <small className="text-success me-2">Copied!</small>}
                        ...{params.value.slice(-4)}
                      </div>
                    );
                  }
                },
                {
                  headerName: 'Created By',
                  field: 'created_by',
                  flex: 1,
                  valueGetter: (params) => {
                    const username = params.data.created_by_user?.username;
                    return username ? `${params.data.created_by} (${username})` : params.data.created_by;
                  }
                },
                {
                  headerName: 'Allowed Origins',
                  field: 'allowed_origins',
                  flex: 1,
                  valueGetter: (params) => {
                    if (!params.value || params.value.length === 0) return 'No restrictions';
                    return `(${params.value.join(', ')})`;
                  }
                },
                {
                  headerName: 'Created At / Expires At',
                  field: 'created_at',
                  width: 200,
                  cellRenderer: (params) => {
                    if (!params.data) return null;
                    return (
                      <div>
                        {new Date(params.data.created_at).toLocaleString()}
                        <br />
                        {new Date(params.data.expires_at).toLocaleString()}
                      </div>
                    );
                  }
                },
                {
                  headerName: 'Status',
                  field: 'is_active',
                  width: 120,
                  cellRenderer: (params) => {
                    const isActive = params.value;
                    return (
                      <span className={`badge ${isActive ? 'bg-success' : 'bg-danger'}`}>
                        {isActive ? 'Active' : 'Inactive'}
                      </span>
                    );
                  }
                },
                {
                  headerName: 'Actions',
                  field: 'api_token_id',
                  width: 200,
                  cellRenderer: (params) => {
                    if (!params.data) return null;
                    const isActive = params.data.is_active;
                    return (
                      <div>
                        {isActive ? (
                          <button 
                            className="btn btn-sm btn-warning me-2" 
                            onClick={() => handleDeactivateToken(params.value)}
                          >
                            Deactivate
                          </button>
                        ) : (
                          <button 
                            className="btn btn-sm btn-success me-2" 
                            onClick={() => handleReactivateToken(params.value)}
                          >
                            Reactivate
                          </button>
                        )}
                        <button 
                          className="btn btn-sm btn-danger" 
                          onClick={() => handleDeleteToken(params.value)}
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    );
                  }
                }
              ]}
              defaultColDef={{
                resizable: true,
                sortable: true,
                filter: true
              }}
              gridOptions={{
                domLayout: 'autoHeight',
                autoSizeStrategy: { type: 'fitGridWidth' }
              }}
              getRowId={(params) => params.data.api_token_id}
            />
        )}</div>
        </div>

        <div className="card-body mt-4">
          <span className="card-title text-primary mb-3">Access Logs</span>
          <div>
            {accessLogsLoading ? (
              <p>Loading access logs...</p>
            ) : accessLogs.length === 0 ? (
              <p>No access logs found for this user.</p>
            ) : (
              <>
                <AgGridTable
                  rowData={accessLogs}
                  height={400}
                  columnDefs={[
                    {
                      headerName: 'Timestamp',
                      field: 'timestamp',
                      width: 200,
                      valueFormatter: (params) => {
                        if (!params.value) return '';
                        return new Date(params.value).toLocaleString();
                      }
                    },
                    {
                      headerName: 'Access Type',
                      field: 'access_type',
                      width: 150
                    },
                    {
                      headerName: 'Result',
                      field: 'result',
                      width: 120,
                      cellRenderer: (params) => {
                        if (!params.value) return null;
                        return (
                          <span className={`badge ${params.value === 'success' ? 'bg-success' : 'bg-danger'}`}>
                            {params.value}
                          </span>
                        );
                      }
                    },
                    {
                      headerName: 'Details',
                      field: 'id',
                      flex: 1,
                      cellRenderer: (params) => {
                        if (!params.data) return null;
                        const details = JSON.stringify(
                          { 
                            ...params.data.authentication_info, 
                            ...params.data.mfa_details, 
                            ...params.data.error_details 
                          }, 
                          null, 
                          2
                        );
                        return (
                          <div className="d-flex align-items-start">
                            <button 
                              className="btn btn-link p-0 me-2" 
                              onClick={() => handleCopy(details, false, `details-${params.data.id}`)} 
                              title="Copy Details"
                            >
                              <i className="fas fa-copy"></i>
                            </button>
                            {copiedTokenId === `details-${params.data.id}` && <small className="text-success me-2">Copied!</small>}
                            <pre className="text-wrap mb-0" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', flex: 1 }}>
                              {details}
                            </pre>
                          </div>
                        );
                      }
                    }
                  ]}
                  defaultColDef={{
                    resizable: true,
                    sortable: true,
                    filter: true
                  }}
                  gridOptions={{
                    domLayout: 'autoHeight',
                    autoSizeStrategy: { type: 'fitGridWidth' }
                  }}
                  getRowId={(params) => params.data.id}
                />
                {accessLogsPagination && (
                  <div className="mt-3">
                    <PaginationControls
                      pagination={accessLogsPagination}
                      onPageChange={handleAccessLogsPageChange}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
  );
};

export default ApiTokenManager;